import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axiosClient from "@service/axiosClient";

export function useAdminPTO(
  statusFilter: string,
  plantFilter: string = "0",
  plants: any[] = [],
  requestPeriodEnabled: boolean = false,
  requestPeriodStart?: string,
  requestPeriodEnd?: string,
  ptoDatesEnabled: boolean = false,
  ptoDatesStart?: string,
  ptoDatesEnd?: string
) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: requests, isLoading } = useQuery({
    queryKey: [
      "admin-pto",
      {
        statusFilter,
        plantFilter,
        plants: plants.map((p) => p.id),
        requestPeriodEnabled,
        requestPeriodStart,
        requestPeriodEnd,
        ptoDatesEnabled,
        ptoDatesStart,
        ptoDatesEnd,
      },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      // If plantFilter is "0" (All), send all plant IDs; otherwise send the selected plant ID
      if (plantFilter === "0" && plants.length > 0) {
        const plantIds = plants.map((p) => p.id).join(",");
        params.append("plant_ids", plantIds);
      } else if (plantFilter !== "0") {
        params.append("plant_ids", plantFilter);
      }

      // Add request period filters if enabled
      if (requestPeriodEnabled && requestPeriodStart && requestPeriodEnd) {
        params.append("request_start_date", requestPeriodStart);
        params.append("request_end_date", requestPeriodEnd);
      }

      // Add PTO dates filters if enabled
      if (ptoDatesEnabled && ptoDatesStart && ptoDatesEnd) {
        params.append("pto_start_date", ptoDatesStart);
        params.append("pto_end_date", ptoDatesEnd);
      }
      setError(null);
      const url = params.toString()
        ? `/pto_api/pto_admin_requests?${params.toString()}`
        : "/pto_api/pto_admin_requests";
      const res = await axiosClient.get(url);
      return res.data.requests || [];
    },
  });

  const approveMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason?: string }) => {
      const res = await axiosClient.post(`/pto_api/admin/approve/${id}`, {
        status: "approved",
        status_reason: reason,
      });
      return res.data;
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["admin-pto"] });
    },
    onError: (e: any) => {
      setError(e.response.data.error || "Failed to approve");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      const res = await axiosClient.post(`/pto_api/admin/reject/${id}`, {
        status: "rejected",
        status_reason: reason,
      });
      return res.data;
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["admin-pto"] });
    },
    onError: (e: any) => setError(e.response.data.error || "Failed to reject"),
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const res = await axiosClient.post(`/pto_api/admin/cancel/${id}`);
      return res.data;
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["admin-pto"] });
    },
    onError: (e: any) => setError(e.response.data.error || "Failed to cancel"),
  });

  const handleReject = async (id: number) => {
    const reason = window.prompt("Reason for rejection:");
    if (!reason) return;
    rejectMutation.mutate({ id, reason });
  };

  return {
    requests,
    isLoading,
    error,
    setError,
    approveMutation,
    rejectMutation,
    cancelMutation,
    handleReject,
  };
}
